import { useState } from "react";
import { Plan } from "@/types/plans";
import { isPlanSelectable } from "./planUtils";
import { usePlans } from "./usePlans";
import { PricingToggle } from "./PricingToggle";
import { PlanCard } from "./PlanCard";
import { PlanCardSkeleton } from "./PlanCardSkeleton";
import { UpgradeDialog } from "./UpgradeDialog";

const SHOW_PRICING = false;

interface PendingUpgrade {
  plan: Plan;
  isUpgrade: boolean;
}

export default function PlanSelector() {
  const {
    loading,
    loadingPlans,
    currentPlan,
    isYearly,
    setIsYearly,
    plans,
    processSubscription,
  } = usePlans();

  const [showUpgradeDialog, setShowUpgradeDialog] = useState<boolean>(false);
  const [pendingUpgrade, setPendingUpgrade] = useState<PendingUpgrade | null>(
    null
  );

  const handleSelectPlan = async (plan: Plan): Promise<void> => {
    const isFree = plan?.name?.toLowerCase() === "free";
    
    if (!SHOW_PRICING && !isFree) {
      window.open("https://calendly.com/k-ketselidis/discovery-call", "_blank");
      return;
    }
    
    if (plan?.name?.toLowerCase() === "enterprise") {
      window.open("https://calendly.com/k-ketselidis/discovery-call", "_blank");
      return;
    }

    if (!isPlanSelectable(currentPlan, plan, isYearly)) {
      return;
    }

    if (plan.name === "free" && currentPlan?.name.toLowerCase() !== "free") {
      setPendingUpgrade({ plan, isUpgrade: false });
      setShowUpgradeDialog(true);
      return;
    }

    if (!currentPlan || currentPlan?.name.toLowerCase() === "free") {
      await processSubscription(plan);
      return;
    }

    setPendingUpgrade({ plan, isUpgrade: true });
    setShowUpgradeDialog(true);
  };

  const confirmUpgrade = async (): Promise<void> => {
    if (!pendingUpgrade) return;
    setShowUpgradeDialog(false);
    await processSubscription(pendingUpgrade.plan);
    setPendingUpgrade(null);
  };

  const cancelUpgrade = (): void => {
    setShowUpgradeDialog(false);
    setPendingUpgrade(null);
  };

  return (
    <div className="px-4 w-auto h-full p-10">
      <div className="mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-2xl font-semibold text-foreground mb-6">
            Choose Your Plan
          </h1>
          <PricingToggle isYearly={isYearly} onToggle={setIsYearly} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mx-auto">
          {loadingPlans
            ? Array.from({ length: 4 }, (_, i) => <PlanCardSkeleton key={i} />)
            : plans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isYearly={isYearly}
                  currentPlan={currentPlan}
                  loading={loading}
                  onSelect={handleSelectPlan}
                  showPricing={SHOW_PRICING}
                />
              ))}
        </div>
      </div>

      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        pendingUpgrade={pendingUpgrade}
        onConfirm={confirmUpgrade}
        onCancel={cancelUpgrade}
      />
    </div>
  );
}
